import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LanguageSwitcher } from "@/components/settings/LanguageSwitcher";
import { cn } from "@/lib/utils";
import {
  segmentedItemActiveClass,
  segmentedItemClass,
  segmentedTrackClass,
} from "@/lib/card-styles";
import { typeMetaClass } from "@/lib/typography";

import type { ProfileMenuThemeProps } from "./types";

export interface ProfileDeviceControlsProps {
  themeProps?: ProfileMenuThemeProps;
}

/**
 * The "this device" group at the foot of the profile menu: language and theme.
 *
 * These two sit together because neither is an account action — both change how
 * the app looks and behaves on this machine, and both are reachable without
 * leaving the page.
 *
 * ## The theme control is a segmented control, not a slider
 *
 * It was an absolutely-positioned pill sliding between three icons, positioned
 * with hand-written `calc()` offsets per state. That maths is fragile — it
 * silently desynced from the segments whenever the track's padding or gap
 * changed — and it animated a pill across a menu that is meant to feel like
 * paper. Painting the *active segment* instead removes the offsets entirely:
 * there is no coordinate to keep in sync, and the state change is a lift rather
 * than a slide.
 *
 * ## It sits under a meta label
 *
 * "This device" is furniture, so it takes `typeMetaClass` (uppercase, tracked,
 * `stone-500`) rather than a `text-sm font-medium` heading. It is orientation,
 * not a subject the learner reads.
 */
export function ProfileDeviceControls({ themeProps }: ProfileDeviceControlsProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5 px-1 pb-1 pt-0.5">
      <p className={cn("px-1.5 pt-1", typeMetaClass)}>
        {t("nav.avatar.device", "This device")}
      </p>

      <div className="px-1">
        <LanguageSwitcher variant="menu" className="px-1.5" />
      </div>

      <div className={cn(segmentedTrackClass, "w-full")} role="group" aria-label={t("nav.avatar.theme.label", "Appearance")}>
        {themeProps
          ? ([
              { value: "light" as const, icon: Sun, label: t("nav.avatar.theme.lightAria", "Light theme") },
              { value: "dark" as const, icon: Moon, label: t("nav.avatar.theme.darkAria", "Dark theme") },
              { value: "system" as const, icon: Monitor, label: t("nav.avatar.theme.systemAria", "System theme") },
            ]).map(({ value, icon: Icon, label }) => {
              const active = themeProps.theme === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  aria-label={label}
                  title={label}
                  onClick={() => themeProps.setTheme(value)}
                  className={cn(segmentedItemClass, active && segmentedItemActiveClass)}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </button>
              );
            })
          : null}
      </div>
    </div>
  );
}

export default ProfileDeviceControls;